// Customer emails in the order modal's Logs tab.
//
// GET /orders/:order_id/activity carries one row per customer email
// attempt (sent / failed) and one per email still queued, with the whole
// email in metadata.email (BE: integrations/services/customerEmailLog.js,
// migration 243). This file renders a row's chips and the detail view a
// click opens: every field we hold about the send, the parcels it listed,
// and the email itself — rendered, as HTML source, and as plain text.
//
// The stored HTML is our own template output with every customer value
// escaped, but it is still customer-influenced (names, product titles), so
// it is only ever shown inside a sandboxed iframe: no scripts, an opaque
// origin, links open in a new tab. "Open in new tab" wraps it in the same
// sandbox rather than opening the raw HTML on the Portal's own origin.

import { useMemo, useState } from "react";
import { X, Copy, ExternalLink } from "lucide-react";
import { toast } from "react-toastify";
import { TEST_EMAIL_LANGUAGES } from "../admin/integrations/types";

type T = (k: string, p?: Record<string, string | number>) => string;

export interface CustomerEmailParcel {
    kind?: string;
    trackingNumber?: string | null;
    carrier?: string | null;
    trackingUrl?: string | null;
    items?: number;
}

export interface CustomerEmailLogEntry {
    status: "sent" | "failed" | "queued";
    kind: "shipment" | "replacement" | "extra_box" | string;
    id?: number;
    orderId?: string | null;
    outboxId?: number | null;
    attempt?: number | null;
    permanent?: boolean;
    to?: string | null;
    from?: string | null;
    fromName?: string | null;
    replyTo?: string | null;
    subject?: string | null;
    language?: string | null;
    transport?: string | null;
    messageId?: string | null;
    error?: string | null;
    details?: {
        parcels?: CustomerEmailParcel[];
        stillToCome?: number;
        consolidatedOrders?: string[];
        trackingScope?: string[] | null;
        titlesTranslated?: number;
        languageNote?: string | null;
        logoShape?: string;
    };
    sentAt?: string;
    html?: string | null;
    text?: string | null;
    bodiesDroppedAt?: string | null;
    piiErasedAt?: string | null;
    // Queued rows only.
    queuedAt?: string;
    sendsAt?: string;
    attempts?: number;
    lastError?: string | null;
    trackingScope?: string[] | null;
}

interface RowLike {
    ts: string;
    metadata: Record<string, unknown> | null;
}

export const customerEmailOf = (row: RowLike): CustomerEmailLogEntry | null => {
    const m = row.metadata as { source?: string; email?: CustomerEmailLogEntry } | null;
    return m?.source === "customer_email" && m.email ? m.email : null;
};

const formatTs = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const languageName = (code?: string | null) => {
    if (!code) return null;
    const hit = TEST_EMAIL_LANGUAGES.find((l) => l.code === code);
    return hit ? `${hit.name} (${code})` : code;
};

// Chips for the Logs table row: who it went to, in what language, and —
// for a queued email — when it goes.
export const customerEmailChips = (email: CustomerEmailLogEntry, t: T) => {
    const chips: { label: string; value: string }[] = [];
    if (email.to) chips.push({ label: t("email_log_chip_to"), value: email.to });
    if (email.language) chips.push({ label: t("email_log_chip_lang"), value: email.language });
    if (email.status === "queued" && email.sendsAt) chips.push({ label: t("email_log_chip_sends"), value: formatTs(email.sendsAt) });
    if (email.status === "failed" && email.error) chips.push({ label: t("email_log_chip_error"), value: email.error.slice(0, 60) });
    if (chips.length === 0) return <span className="logs-context-empty">—</span>;
    return (
        <span className="logs-context-chips">
            {chips.map((c) => (
                <span key={c.label} className="logs-context-chip">
                    <span className="logs-context-chip-label">{c.label}:</span>
                    <span className="logs-context-chip-value">{c.value}</span>
                </span>
            ))}
        </span>
    );
};

// A complete document for the sandboxed frame. <base target="_blank"> so a
// tracking link opens a new tab instead of trying to navigate the frame.
const previewDocument = (html: string) =>
    `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">`
    + `<meta name="viewport" content="width=device-width,initial-scale=1"></head>`
    + `<body style="margin:0">${html}</body></html>`;

const escapeAttr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

// The email in a new tab, still sandboxed: a blob page that holds only an
// iframe whose srcdoc is the (attribute-escaped) email.
const openInNewTab = (html: string, title: string) => {
    const page = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeAttr(title)}</title>`
        + `<style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style></head>`
        + `<body><iframe sandbox="allow-popups allow-popups-to-escape-sandbox" srcdoc="${escapeAttr(previewDocument(html))}"></iframe></body></html>`;
    const url = URL.createObjectURL(new Blob([page], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const copy = async (value: string, t: T) => {
    try {
        await navigator.clipboard.writeText(value);
        toast.success(t("email_log_copied"), { toastId: "email-log-copied" });
    } catch {
        toast.error(t("email_log_copy_failed"), { toastId: "email-log-copy-failed" });
    }
};

type View = "preview" | "html" | "text";

const CustomerEmailLogModal = ({
    action, email, onClose, t,
}: {
    action: string;
    email: CustomerEmailLogEntry;
    onClose: () => void;
    t: T;
}) => {
    const [view, setView] = useState<View>("preview");
    const hasBodies = Boolean(email.html || email.text);
    const srcDoc = useMemo(() => (email.html ? previewDocument(email.html) : ""), [email.html]);
    const parcels = email.details?.parcels ?? [];
    const queued = email.status === "queued";

    const statusLabel = queued
        ? t("email_log_status_queued")
        : email.status === "sent"
            ? t("email_log_status_sent")
            : email.permanent ? t("email_log_status_failed_permanent") : t("email_log_status_failed_retry");
    const statusClass = queued ? "pending" : email.status === "sent" ? "success" : "error";

    return (
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            <div className="modal-card logs-detail-modal email-log-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{action}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <dl className="logs-detail-grid">
                        <dt>{t("email_log_status")}</dt>
                        <dd><span className={`logs-status logs-status--${statusClass}`}>{statusLabel}</span></dd>
                        <dt>{t("email_log_type")}</dt>
                        <dd>{t(`email_log_kind_${email.kind}`)}</dd>
                        {queued ? (
                            <>
                                <dt>{t("email_log_queued_at")}</dt><dd>{formatTs(email.queuedAt)}</dd>
                                <dt>{t("email_log_sends_at")}</dt><dd>{formatTs(email.sendsAt)}</dd>
                                {email.attempts ? <><dt>{t("email_log_attempts")}</dt><dd>{email.attempts}</dd></> : null}
                                {email.lastError ? <><dt>{t("email_log_last_error")}</dt><dd className="email-log-error">{email.lastError}</dd></> : null}
                            </>
                        ) : (
                            <>
                                <dt>{email.status === "sent" ? t("email_log_sent_at") : t("email_log_attempted_at")}</dt>
                                <dd>{formatTs(email.sentAt)}</dd>
                                {email.error ? <><dt>{t("email_log_error")}</dt><dd className="email-log-error">{email.error}</dd></> : null}
                                <dt>{t("email_log_to")}</dt><dd>{email.to ?? (email.piiErasedAt ? t("email_log_erased") : "—")}</dd>
                                <dt>{t("email_log_from")}</dt>
                                <dd>{email.fromName ? `${email.fromName} <${email.from ?? "—"}>` : (email.from ?? "—")}</dd>
                                <dt>{t("email_log_reply_to")}</dt><dd>{email.replyTo ?? "—"}</dd>
                                <dt>{t("email_log_subject")}</dt><dd>{email.subject ?? "—"}</dd>
                                <dt>{t("email_log_language")}</dt>
                                <dd>
                                    {languageName(email.language) ?? "—"}
                                    {email.details?.titlesTranslated
                                        ? <span className="logs-detail-muted"> · {t("email_log_titles_translated", { n: email.details.titlesTranslated })}</span>
                                        : null}
                                    {email.details?.languageNote
                                        ? <div className="logs-detail-muted">{t("email_log_language_note", { reason: email.details.languageNote })}</div>
                                        : null}
                                </dd>
                                <dt>{t("email_log_message_id")}</dt>
                                <dd>
                                    {email.messageId ? (
                                        <>
                                            <code>{email.messageId}</code>{" "}
                                            <button type="button" className="email-log-icon-btn" onClick={() => copy(email.messageId!, t)}
                                                title={t("email_log_copy")} aria-label={t("email_log_copy")}>
                                                <Copy size={13} />
                                            </button>
                                        </>
                                    ) : "—"}
                                </dd>
                                <dt>{t("email_log_delivery")}</dt>
                                <dd>
                                    {[email.transport === "ses-api" ? "Amazon SES" : email.transport,
                                        email.attempt ? t("email_log_attempt_n", { n: email.attempt }) : null,
                                        email.outboxId ? t("email_log_outbox_row", { id: email.outboxId }) : null,
                                    ].filter(Boolean).join(" · ") || "—"}
                                </dd>
                            </>
                        )}
                    </dl>

                    {(parcels.length > 0 || email.details?.stillToCome || email.details?.consolidatedOrders?.length) ? (
                        <>
                            <h3 className="logs-detail-subhead">{t("email_log_contents")}</h3>
                            {parcels.length > 0 && (
                                <table className="email-log-parcels">
                                    <thead>
                                        <tr>
                                            <th>{t("email_log_parcel")}</th>
                                            <th>{t("email_log_carrier")}</th>
                                            <th>{t("email_log_tracking")}</th>
                                            <th>{t("email_log_units")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parcels.map((p, i) => (
                                            <tr key={`${p.trackingNumber ?? "p"}-${i}`}>
                                                <td>{p.kind === "pallet" ? t("email_log_pallet") : t("email_log_parcel")} {i + 1}</td>
                                                <td>{p.carrier || "—"}</td>
                                                <td>
                                                    {p.trackingNumber
                                                        ? (p.trackingUrl
                                                            ? <a href={p.trackingUrl} target="_blank" rel="noopener noreferrer">{p.trackingNumber}</a>
                                                            : p.trackingNumber)
                                                        : "—"}
                                                </td>
                                                <td>{p.items ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <dl className="logs-detail-grid email-log-extra">
                                {email.details?.stillToCome ? (
                                    <><dt>{t("email_log_still_to_come")}</dt><dd>{t("email_log_units_n", { n: email.details.stillToCome })}</dd></>
                                ) : null}
                                {email.details?.consolidatedOrders?.length ? (
                                    <><dt>{t("email_log_orders_in_shipment")}</dt><dd>{email.details.consolidatedOrders.join(", ")}</dd></>
                                ) : null}
                            </dl>
                        </>
                    ) : null}

                    {!queued && (
                        <>
                            <h3 className="logs-detail-subhead">{t("email_log_the_email")}</h3>
                            {!hasBodies ? (
                                <p className="logs-detail-muted">
                                    {email.piiErasedAt
                                        ? t("email_log_bodies_erased")
                                        : email.bodiesDroppedAt
                                            ? t("email_log_bodies_dropped")
                                            : t("email_log_bodies_none")}
                                </p>
                            ) : (
                                <>
                                    <div className="email-log-viewbar">
                                        <div className="dispatch-detail-tabs email-log-tabs" role="tablist">
                                            {(["preview", "html", "text"] as View[]).map((v) => (
                                                <button key={v} type="button" role="tab" aria-selected={view === v}
                                                    className={`dispatch-detail-tab${view === v ? " dispatch-detail-tab--active" : ""}`}
                                                    disabled={(v === "text" && !email.text) || (v !== "text" && !email.html)}
                                                    onClick={() => setView(v)}>
                                                    {t(`email_log_view_${v}`)}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="email-log-actions">
                                            {email.html && (
                                                <button type="button" className="btn btn-ghost btn-sm"
                                                    onClick={() => openInNewTab(email.html!, email.subject ?? "Email")}>
                                                    <ExternalLink size={14} /> {t("email_log_open_tab")}
                                                </button>
                                            )}
                                            <button type="button" className="btn btn-ghost btn-sm"
                                                onClick={() => copy(view === "text" ? (email.text ?? "") : (email.html ?? ""), t)}>
                                                <Copy size={14} /> {view === "text" ? t("email_log_copy_text") : t("email_log_copy_html")}
                                            </button>
                                        </div>
                                    </div>
                                    {view === "preview" && email.html && (
                                        <iframe
                                            className="email-log-frame"
                                            title={email.subject ?? "Email"}
                                            sandbox="allow-popups allow-popups-to-escape-sandbox"
                                            srcDoc={srcDoc}
                                        />
                                    )}
                                    {view === "html" && email.html && <pre className="logs-detail-meta email-log-source">{email.html}</pre>}
                                    {view === "text" && email.text && <pre className="logs-detail-meta email-log-source">{email.text}</pre>}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerEmailLogModal;
